// src/components/GoalsSummary.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Stack,
    Button,
    Spinner,
    Progress,
    useToast,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Flex,
    IconButton,
    Badge,
    List,
    ListItem,
    Divider
} from '@chakra-ui/react';
import { EditIcon, CheckIcon, DeleteIcon } from '@chakra-ui/icons';
import { useAuth } from '../../../contexts/AuthContext';
import GoalForm from './GoalForm';
import { fetchGoals, getProgressMap, updateGoal, deleteGoal } from '../../../data/Goals';

export default function GoalsSummary() {
    const { user } = useAuth();
    const toast = useToast();

    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [progressMap, setProgressMap] = useState({});
    const [showCompleted, setShowCompleted] = useState(false);

    // Fetch all goals
    const loadGoals = async () => {
        setLoading(true);
        const { data, error } = await fetchGoals(user.id);
        setLoading(false);
        if (error) toast({ title: 'Error loading goals', description: error.message, status: 'error' });
        else setGoals(data || []);
    };

    useEffect(() => {
        if (user) loadGoals();
    }, [user]);

    // Compute progress for numeric goals
    useEffect(() => {
        const compute = async () => {
            const { map, error } = await getProgressMap(user.id, goals);
            if (error) toast({ title: 'Error computing progress', description: error.message, status: 'error' });
            else setProgressMap(map);
        };
        if (goals.length) compute();
    }, [goals, user, toast]);

    const handleEdit = (goal) => {
        setEditingGoal(goal);
        setShowForm(true);
    };

    const handleMarkComplete = async (goal) => {
        const { error } = await updateGoal(goal.id, { status: 'completed' });
        if (error) {
            toast({ title: 'Error', description: error.message, status: 'error' });
        } else {
            setGoals(gs => gs.map(g => g.id === goal.id ? { ...g, status: 'completed' } : g));
            toast({ title: 'Goal completed', status: 'success' });
        }
    };

    const handleDelete = async (goal) => {
        if (!window.confirm('Are you sure you want to delete this goal?')) return;
        const { error } = await deleteGoal(goal.id);
        if (error) toast({ title: 'Error deleting', description: error.message, status: 'error' });
        else {
            setGoals(gs => gs.filter(g => g.id !== goal.id));
            toast({ title: 'Goal deleted', status: 'success' });
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" py={10}>
                <Spinner size="xl" />
            </Box>
        );
    }

    const activeGoals = goals.filter(g => g.status !== 'completed');
    const completedGoals = goals.filter(g => g.status === 'completed');

    return (
        <Stack spacing={6} p={6} bg="white" rounded="lg" shadow="sm">
            {/* Active Goals */}
            {activeGoals.length === 0 ? (
                <Box textAlign="center">
                    <Heading size="md" mb={4}>No active goals</Heading>
                    <Text mb={4}>Create your first goal to get started.</Text>
                    <Button colorScheme="blue" onClick={() => setShowForm(true)}>Create Goal</Button>
                </Box>
            ) : (
                activeGoals.map(goal => (
                    <Box key={goal.id} p={4} bg="gray.50" rounded="md" shadow="xs">
                        <Flex justify="space-between" align="center">
                            <Heading size="sm">{goal.title}</Heading>
                            <Flex>
                                <IconButton aria-label="Complete" icon={<CheckIcon />} size="sm" mr={2} onClick={() => handleMarkComplete(goal)} />
                                <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" mr={2} onClick={() => handleEdit(goal)} />
                                <IconButton aria-label="Delete" icon={<DeleteIcon />} size="sm" onClick={() => handleDelete(goal)} />
                            </Flex>
                        </Flex>
                        {goal.goal_type === 'numeric' && (
                            <>
                                <Text fontSize="sm" mt={2}>
                                    {progressMap[goal.id]?.progress ?? 0} {goal.target_unit} / {goal.target_value} {goal.target_unit}
                                </Text>
                                <Progress value={progressMap[goal.id]?.percent ?? 0} size="sm" mt={2} />
                            </>
                        )}
                    </Box>
                ))
            )}

            {/* Divider & Completed Goals */}
            {completedGoals.length > 0 && (
                <>
                    <Divider borderColor="gray.200" />
                    <Accordion allowToggle>
                        <AccordionItem border="none">
                            <AccordionButton px={0} onClick={() => setShowCompleted(!showCompleted)}>
                                <Flex flex="1" align="center">
                                    <Text fontWeight="bold">
                                        {showCompleted ? 'Hide' : 'Show'} Completed Goals
                                    </Text>
                                    <Badge ml={2} variant="subtle" colorScheme="green">
                                        {completedGoals.length}
                                    </Badge>
                                </Flex>
                                <AccordionIcon />
                            </AccordionButton>

                            <AccordionPanel px={0} pt={2}>
                                <List spacing={2}>
                                    {completedGoals.map(goal => (
                                        <ListItem key={goal.id} pl={2}>
                                            <Text as="s" color="gray.500">
                                                {goal.title}
                                            </Text>
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </>
            )}

            {/* Add/Edit Form */}
            <Button colorScheme="blue" onClick={() => { setEditingGoal(null); setShowForm(true); }}>
                {editingGoal ? 'Edit Goal' : 'Add New Goal'}
            </Button>
            {showForm && (
                <Box mt={4}>
                    <GoalForm
                        initialData={editingGoal}
                        onSuccess={() => { setShowForm(false); setEditingGoal(null); loadGoals(); }}
                    />
                </Box>
            )}
        </Stack>
    );
}
